import {
  type DisplayMode,
  type LinkPolicy,
  type TenantModels,
  type TenantSettings,
  TenantSettingsSchema,
  VENDOR_INSTRUCTIONS_MAX_CHARS,
} from '@plato/shared';
import { useState } from 'react';
import { ActionError, ReadOnlyNote, useAction } from '../../components/action.tsx';
import { Loaded } from '../../components/page.tsx';
import { useConsole, useHasRole, useReloadableTenantQuery } from '../../data/console-context.tsx';
import type { ConsoleData, SettingsChanges, TenantSummary } from '../../data/console-data.ts';

const loadSettings = (data: ConsoleData, tenant: TenantSummary) =>
  data.getAssistantSettings(tenant.id);

const LINK_POLICIES: ReadonlyArray<{ value: LinkPolicy; label: string }> = [
  { value: 'vendor_origins', label: 'Open links to your own sites; copy any other' },
  { value: 'copy_only', label: 'Never open links; offer to copy them' },
  { value: 'confirm', label: 'Ask before opening any other site' },
];
const DISPLAY_MODES: ReadonlyArray<{ value: DisplayMode; label: string }> = [
  { value: 'floating', label: 'Floating panel' },
  { value: 'inline', label: 'Inline in your page' },
];

export function BehaviourPage() {
  const { query, reload } = useReloadableTenantQuery(loadSettings);
  return (
    <Loaded query={query}>
      {(settings) => (
        <SettingsForm key={JSON.stringify(settings)} settings={settings} onSaved={reload} />
      )}
    </Loaded>
  );
}

function SettingsForm(props: { settings: TenantSettings; onSaved: () => void }) {
  const { data, tenant } = useConsole();
  const canEdit = useHasRole('editor');
  const [draft, setDraft] = useState(props.settings);
  const action = useAction();
  const set = <K extends keyof TenantSettings>(key: K, value: TenantSettings[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const changed = changedFields(props.settings, withoutBlankInstructions(draft));
  const valid = TenantSettingsSchema.safeParse(withoutBlankInstructions(draft)).success;

  return (
    <form
      className="form"
      onSubmit={(event) => {
        event.preventDefault();
        void action
          .run(async () => {
            await data.updateAssistantSettings(tenant.id, changed);
          })
          .then((ok) => ok && props.onSaved());
      }}
    >
      {!canEdit && <ReadOnlyNote needs="editor" />}
      <fieldset disabled={!canEdit || action.pending}>
        <label>
          Name
          <input
            value={draft.assistantName}
            maxLength={80}
            required
            onChange={(e) => set('assistantName', e.target.value)}
          />
        </label>
        <label>
          AI label
          <span className="hint">
            Always shown next to the assistant's replies. Reword it; it cannot be removed.
          </span>
          <input
            value={draft.aiLabel}
            maxLength={80}
            required
            onChange={(e) => set('aiLabel', e.target.value)}
          />
        </label>
        <label>
          First message notice
          <textarea
            value={draft.firstMessageNotice}
            maxLength={500}
            required
            rows={3}
            onChange={(e) => set('firstMessageNotice', e.target.value)}
          />
        </label>
        <label>
          Your instructions
          <span className="hint">
            Added after Plato's own instructions, which take precedence. Tell the assistant about
            your product, your users and the tone to use. {draft.vendorInstructions?.length ?? 0} /{' '}
            {VENDOR_INSTRUCTIONS_MAX_CHARS} characters; leave it empty for none.
          </span>
          <textarea
            value={draft.vendorInstructions ?? ''}
            maxLength={VENDOR_INSTRUCTIONS_MAX_CHARS}
            rows={6}
            onChange={(e) => set('vendorInstructions', e.target.value)}
          />
        </label>
        <label>
          Links in replies
          <select
            value={draft.linkPolicy}
            onChange={(e) => set('linkPolicy', e.target.value as LinkPolicy)}
          >
            {LINK_POLICIES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <fieldset className="choices">
          <legend>Where it can appear</legend>
          {DISPLAY_MODES.map((mode) => (
            <label key={mode.value} className="checkbox">
              <input
                type="checkbox"
                checked={draft.displayModes.includes(mode.value)}
                onChange={(e) =>
                  set(
                    'displayModes',
                    e.target.checked
                      ? [...draft.displayModes, mode.value]
                      : draft.displayModes.filter((value) => value !== mode.value),
                  )
                }
              />
              {mode.label}
            </label>
          ))}
        </fieldset>
        <label>
          Colour scheme
          <select
            value={draft.appearance.colorScheme ?? 'auto'}
            onChange={(e) =>
              set('appearance', {
                ...draft.appearance,
                colorScheme: e.target.value as 'light' | 'dark' | 'auto',
              })
            }
          >
            <option value="auto">Follow the user's device</option>
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </select>
        </label>
        <label>
          Keep conversations for (days)
          <input
            type="number"
            min={1}
            value={draft.retentionDays}
            onChange={(e) => set('retentionDays', Number(e.target.value))}
          />
        </label>
        <div>
          <span className="label">Models</span>
          <p className="muted">{describeModels(draft.models)} (set by Plato)</p>
        </div>
      </fieldset>
      {canEdit && (
        <div className="row">
          <button
            type="submit"
            className="primary"
            disabled={action.pending || Object.keys(changed).length === 0 || !valid}
          >
            {action.pending ? 'Saving…' : 'Save changes'}
          </button>
          {Object.keys(changed).length > 0 && (
            <button type="button" className="quiet" onClick={() => setDraft(props.settings)}>
              Discard
            </button>
          )}
        </div>
      )}
      <ActionError error={action.error} />
    </form>
  );
}

function changedFields(saved: TenantSettings, draft: TenantSettings): SettingsChanges {
  const out: Record<string, unknown> = {};
  const keys = new Set([...Object.keys(saved), ...Object.keys(draft)]) as Set<keyof TenantSettings>;
  for (const key of keys) {
    if (key === 'models') continue;
    if (JSON.stringify(saved[key]) !== JSON.stringify(draft[key])) out[key] = draft[key] ?? null;
  }
  return out as SettingsChanges;
}

function withoutBlankInstructions(draft: TenantSettings): TenantSettings {
  const text = draft.vendorInstructions;
  return { ...draft, vendorInstructions: text?.trim() ? text : undefined };
}

function describeModels(models: TenantModels | undefined): string {
  if (models === undefined) return 'Platform default';
  return models.choices
    .map((choice) => (choice.id === models.default ? `${choice.label} (default)` : choice.label))
    .join(', ');
}
